import { isProjectCustomizationTextElementView, TextElementView } from '../types/ElementView';

/**
 * Extracts the font attributes from either the Template or the Customization view
 * which put these attributes in different places.
 *
 * Here is what the backend told us:
 *  - Template data will have everything font related under the font attribute.
 *  - For customization elements, some of the attributes that are global are under
 *    the font attributes, but the customizable attributes (font size, alignment,
 *    etc) are directly in the element object and won't exist in the font attributes.
 *
 * Practically speaking, it comes down to 6 attributes live in one of two different
 * depending on what type of Card we are displaying.  The 6 attributes are:
 * - character_spacing
 * - font_color
 * - font_size
 * - horizontal_alignment
 * - line_height
 * - vertical_alignment
 *
 *
 * @example
 * <caption>WCardTemplateElementView uses WCardElementFontStyleView for the font attribute</caption>
 * declare class WCardTemplateElementView {
 *  'id'?: number;
 *   'fields'?: Array<WCardTemplateElementFieldView>;
 *   'font'?: WCardElementFontStyleView;
 * }
 *
 * @example
 * <caption>WCardElementFontStyleView has the 6 font attributes</caption>
 * declare class WCardElementFontStyleView {
 *  'font_size'?: number;
 *  'line_height'?: number;
 *  'character_spacing'?: number;
 *  'font_color'?: string;
 *  'horizontal_alignment'?: string;
 *  'vertical_alignment'?: string;
 * }
 * </code>
 *
 * @Example
 * WProjectCustomizationElementView uses WElementFontView as the font attribute
 * and has the 6 attributes at the root.
 *
 * declare class WProjectCustomizationElementView {
 *  'font'?: WElementFontView;
 *  'font_color'?: string;
 *  'font_size'?: number;
 *  'line_height'?: number;
 *  'character_spacing'?: number;
 *  'horizontal_alignment'?: string;
 *  'vertical_alignment'?: string;
 * }
 *
 * @see WCardElementFontStyleView
 * @see WCardTemplateElementView
 * @see WElementFontView
 * @see WProjectCustomizationElementView
 *
 * @param textElement
 */
const getUniqueFontAttributes = (textElement: TextElementView) => {
  if (isProjectCustomizationTextElementView(textElement)) {
    const {
      character_spacing: characterSpacing,
      font_color,
      font_size,
      horizontal_alignment: horizontalAlignment,
      line_height,
      vertical_alignment: verticalAlignment,
    } = textElement;
    return {
      characterSpacing,
      fontColor: font_color as string,
      fontSize: font_size as number,
      horizontalAlignment,
      lineHeight: line_height as number,
      verticalAlignment,
    };
  }

  const {
    character_spacing: characterSpacing,
    font_color,
    font_size,
    horizontal_alignment: horizontalAlignment,
    line_height,
    vertical_alignment: verticalAlignment,
  } = textElement.font;
  return {
    characterSpacing,
    fontColor: font_color as string,
    fontSize: font_size as number,
    horizontalAlignment,
    lineHeight: line_height as number,
    verticalAlignment,
  };
};

type FontAttributes = {
  characterSpacing: number | undefined;
  fontColor: string;
  fontFamily: string;
  fontSize: number;
  fontStyle: string | undefined;
  fontWeight: number;
  horizontalAlignment: string | undefined;
  letterSpacing: number;
  lineHeight: number;
  verticalAlignment: string | undefined;
};

/**
 * Extract font attributes from from the TextElementView.  For the PDP, we have a template
 * which has the font attributes in the font object.  For the customization view, the font
 * attributes on at the root.
 *
 * @see getUniqueFontAttributes
 */
export const getFontAttributesFromTextElementView = (
  textElement: TextElementView
): FontAttributes => {
  const fontAttributes = getUniqueFontAttributes(textElement);

  const {
    font_family: fontFamily,
    font_weight: fontWeight,
    font_style: fontStyle,
  } = textElement.font;

  return {
    ...fontAttributes,
    fontFamily: fontFamily as string,
    fontStyle,
    fontWeight: fontWeight as number,
    letterSpacing: fontAttributes.characterSpacing ? fontAttributes.characterSpacing * 0.001 : 0,
  };
};
